// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
// configuration parameters are located at authConfig.js
import * as authPopup from '@/auth/authPopup'
import b2cPolicies from '@/auth/policies'
import * as msal from '@azure/msal-browser'
import authConfig from '@/auth/authConfig'
import {msalAgent} from "@/auth/authPopup";
// import {useMsalStore} from '@/store/msal'

// const msalStore = useMsalStore();

// Note: msalAgent is imported from authPopup.js and has the ACQUIRE_TOKEN_SUCCESS
// event handler already registered. This event handler will automatically update
// msalStore.accessToken for both popup and redirect authentication flows.

let accountId = ""
const MAX_AUTH_RETRIES = 2
const retryKey = 'auth_retry_count'

// Process a pending B2C redirect response. Called from main.js after initMsal()
// (and before mount) so the agent exists and the redirect hash is handled before
// the app renders; previously ran at module-eval, which is no longer safe now
// that the agent is created lazily (events-app#913).
export function handleRedirect() {
    const redirectPromise = authPopup.msalAgent.handleRedirectPromise()
        .then(async response => {
            if (response) {
                await handleResponse(response)
            }
        })
        .catch(error => {
            console.log(error)
        });
    selectAccount() // in case of page refresh
    return redirectPromise
}


function setAccount(account) {
    accountId = account.homeAccountId
    authPopup.msalAgent.setActiveAccount(account)
}

function selectAccount() {

    /**
     * See here for more information on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    const currentAccounts = authPopup.msalAgent.getAllAccounts()

    if (currentAccounts.length < 1) {
        return
    } else if (currentAccounts.length > 1) {

        /**
         * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
         * is cached as a new account, which results in more than one account in the cache. Here we make
         * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
         * as this is the default flow the user initially signed-in with.
         */
        const accounts = currentAccounts.filter(account =>
            account.homeAccountId.toUpperCase().includes(b2cPolicies.names.signUpSignIn.toUpperCase())
            &&
            account.idTokenClaims
            &&
            account.idTokenClaims.iss.toUpperCase().includes(b2cPolicies.authorityDomain.toUpperCase())
            &&
            account.idTokenClaims.aud === authConfig.msalConfig.auth.clientId
        )

        if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (accounts.every(account => account.localAccountId === accounts[0].localAccountId)) {
                // All accounts belong to the same user
                setAccount(accounts[0])
            } else {
                // Multiple users detected. Logout all to be safe.
                signOut()
            }
        } else if (accounts.length === 1) {
            setAccount(accounts[0])
        }

    } else if (currentAccounts.length === 1) {
        setAccount(currentAccounts[0])
    }
}

async function handleResponse(response) {

    /**
     * To see the full list of response object properties, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
     */

    if (response !== null) {
        setAccount(response.account)
    } else {
        selectAccount()
    }
}

function signIn() {

    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    const request = {...authConfig.loginRequest}
    const returnUrl = sessionStorage.getItem('signin_return_url')
    if (returnUrl) {
        sessionStorage.removeItem('signin_return_url')
        request.redirectStartPage = returnUrl
    } else if (window.location.pathname === '/require-signin') {
        request.redirectStartPage = '/'
    }
    authPopup.msalAgent.loginRedirect(request)
}

async function signOut() {

    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */
    const logoutRequest = {
        postLogoutRedirectUri: authConfig.msalConfig.auth.redirectUri + '?signout=true'
    }

    authPopup.msalAgent.logoutRedirect(logoutRequest)
}

async function getTokenRedirect(request, accountId) {
    /**
     * See here for more information on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    request.account = msalAgent.getAccountByHomeId(accountId)

    /**
     *
     */
    return msalAgent.acquireTokenSilent(request)
        .then(async (response) => {
            // In case the response from B2C server has an empty accessToken field
            // throw an error to initiate token acquisition
            if (!response.accessToken || response.accessToken == "") {
                throw new msal.InteractionRequiredAuthError
            }
            // Note: msalStore.accessToken is automatically updated by ACQUIRE_TOKEN_SUCCESS event handler
            return response // success
        })
        .catch(error => {
            if (error instanceof msal.InteractionRequiredAuthError) {
                // fallback to interaction when silent call fails
                console.warn('Silent token acquisition fails. Acquiring token using redirect.' + error)
                return msalAgent.acquireTokenRedirect(request)
                    .then(response => {
                        if (!response.accessToken || response.accessToken == "") {
                            console.warn('Redirect token acquisition fails. Aborting API access. \n')
                        } else {
                            // Note: msalStore.accessToken is automatically updated by ACQUIRE_TOKEN_SUCCESS event handler
                            return response // second try success
                        }
                    }).catch(async error => {
                        const retries = parseInt(sessionStorage.getItem(retryKey) || '0')
                        console.error('Token acquisition failed:', error)
                        if (retries < MAX_AUTH_RETRIES) {
                            sessionStorage.setItem(retryKey, (retries + 1).toString())
                            await signOut()
                            signIn()
                        } else {
                            sessionStorage.removeItem(retryKey)
                            signOut()
                            if (window.location.pathname !== '/require-signin') {
                                sessionStorage.setItem('signin_return_url', window.location.pathname + window.location.search)
                            }
                            window.location.href = '/require-signin?message=Session%20has%20expired'
                        }
                    });
            } else {
                return ""
            }
        });
}

// Acquires an access token and then passes it to the API call
async function passTokenToApi() {
    const accounts = msalAgent.getAllAccounts()
    if (accounts.length < 1) {
        // we are not here because of a login attempt
        return {}
    }
    accountId = accounts[0].homeAccountId

    let tokenResponse = await getTokenRedirect(authConfig.tokenRequest, accountId)
        .then(async response => {
            if (!response.accessToken || '' == response.accessToken) {
                return false
            }
            console.log('access_token acquired at: ' + new Date().toString())
            // Note: msalStore.accessToken, apiStore.oid, and apiStore.isStaff are all
            // automatically updated by the ACQUIRE_TOKEN_SUCCESS event handler
            return response
        }).catch((error) => {
            console.error(error)
            return false
        });
    return tokenResponse
}

/**
 * To initiate a B2C user-flow, simply make a login request using
 * the full authority string of that user-flow e.g.
 * https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_edit_profile_v2
 */
function editProfile() {


    const editProfileRequest = b2cPolicies.authorities.editProfile
    editProfileRequest.loginHint = authPopup.msalAgent.getAccountByHomeId(accountId).username

    authPopup.msalAgent.loginRedirect(editProfileRequest)
}


export {editProfile, getTokenRedirect, passTokenToApi, signIn, signOut}